import type { SyncProvider, SharedFolderInfo } from './syncProvider.js';
import type { RemoteFile } from './types.js';
import {
  signIn, signOut as authSignOut, trySilentResume,
  getAccessToken, getAccountEmail, refreshToken,
  hasSharedConsent, requestSharedScope, getSharedToken, invalidateSharedToken,
} from './googleAuth.js';

/**
 * Drive REST API v3 backend for SyncProvider. Mirrors the Kotlin
 * GoogleDriveSyncProvider — same appDataFolder scope, same path-to-folder-ID
 * resolution, same retry-on-transient-error behavior.
 *
 * Implementation notes:
 *   - Drive REST endpoints used:
 *       GET  drive/v3/files?spaces=appDataFolder&q=...   (list/find)
 *       GET  drive/v3/files/{id}?alt=media               (read)
 *       POST upload/drive/v3/files?uploadType=multipart  (create+content)
 *       PATCH upload/drive/v3/files/{id}?uploadType=media (update content)
 *       DELETE drive/v3/files/{id}                       (delete)
 *   - On 401 we refresh the access token once and retry — covers the 1-hour
 *     token expiry without forcing the user to reauthorize.
 *   - Folder IDs are cached for the session (cleared on signOut).
 */

const DRIVE_API = 'https://www.googleapis.com/drive/v3';
const UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3';
const FOLDER_MIME = 'application/vnd.google-apps.folder';
const APP_DATA = 'appDataFolder';
const DEFAULT_MIME = 'application/json';

interface DriveFile {
  id: string;
  name: string;
  mimeType?: string;
  modifiedTime?: string;
  size?: string;
  parents?: string[];
}

export class GoogleDriveSyncProvider implements SyncProvider {
  private folderIdCache = new Map<string, string>();

  getProviderName(): string { return 'google_drive'; }
  isAuthenticated(): boolean { return getAccessToken() !== null; }
  getAccountEmail(): string | null { return getAccountEmail(); }

  async authenticate(): Promise<boolean> { return signIn(); }
  async trySilentResume(): Promise<boolean> { return trySilentResume(); }

  async signOut(): Promise<void> {
    this.folderIdCache.clear();
    await authSignOut();
  }

  // ─── HTTP plumbing ───────────────────────────────────────────────────────

  /** GETs JSON from a Drive REST endpoint, with token refresh + retry on 401. */
  private async getJson<T>(url: string): Promise<T> {
    const doFetch = async (): Promise<Response> => fetch(url, {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    let resp = await doFetch();
    if (resp.status === 401 && await refreshToken()) resp = await doFetch();
    if (!resp.ok) throw new Error(`Drive GET ${url} → ${resp.status} ${await resp.text()}`);
    return resp.json() as Promise<T>;
  }

  /** GETs raw text for file media downloads. */
  private async getText(url: string): Promise<string> {
    const doFetch = async (): Promise<Response> => fetch(url, {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    let resp = await doFetch();
    if (resp.status === 401 && await refreshToken()) resp = await doFetch();
    if (!resp.ok) throw new Error(`Drive GET ${url} → ${resp.status}`);
    return resp.text();
  }

  /**
   * Multipart upload — used for create-with-content. Drive's multipart spec
   * requires a precise body format: two MIME parts separated by a boundary,
   * first part is JSON metadata, second is the file content.
   */
  private async multipartCreate(metadata: object, content: string): Promise<DriveFile> {
    const boundary = '----shellsync_' + crypto.randomUUID();
    const body =
      `--${boundary}\r\n` +
      `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
      `${JSON.stringify(metadata)}\r\n` +
      `--${boundary}\r\n` +
      `Content-Type: ${DEFAULT_MIME}; charset=UTF-8\r\n\r\n` +
      `${content}\r\n` +
      `--${boundary}--`;

    const doFetch = async (): Promise<Response> => fetch(
      `${UPLOAD_API}/files?uploadType=multipart&fields=id,name,parents`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
          'Content-Type': `multipart/related; boundary=${boundary}`,
        },
        body,
      });
    let resp = await doFetch();
    if (resp.status === 401 && await refreshToken()) resp = await doFetch();
    if (!resp.ok) throw new Error(`Drive multipart create → ${resp.status} ${await resp.text()}`);
    return resp.json() as Promise<DriveFile>;
  }

  /** Simple media-only update for existing files (content overwrite). */
  private async mediaUpdate(fileId: string, content: string): Promise<void> {
    const url = `${UPLOAD_API}/files/${fileId}?uploadType=media`;
    const doFetch = async (): Promise<Response> => fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${getAccessToken() ?? ''}`,
        'Content-Type': `${DEFAULT_MIME}; charset=UTF-8`,
      },
      body: content,
    });
    let resp = await doFetch();
    if (resp.status === 401 && await refreshToken()) resp = await doFetch();
    if (!resp.ok) throw new Error(`Drive media update → ${resp.status} ${await resp.text()}`);
  }

  // ─── Path/folder resolution ──────────────────────────────────────────────

  /**
   * Walks a slash-separated folder path within appDataFolder, creating
   * intermediate folders as needed. Returns the deepest folder's ID.
   * Empty path → "appDataFolder".
   */
  private async resolveFolderId(folderPath: string): Promise<string> {
    const trimmed = folderPath.replace(/^\/+|\/+$/g, '');
    if (!trimmed) return APP_DATA;

    const cached = this.folderIdCache.get(trimmed);
    if (cached) return cached;

    let parentId = APP_DATA;
    const parts = trimmed.split('/').filter(Boolean);
    const accumulated: string[] = [];

    for (const segment of parts) {
      accumulated.push(segment);
      const cacheKey = accumulated.join('/');
      const fromCache = this.folderIdCache.get(cacheKey);
      if (fromCache) { parentId = fromCache; continue; }

      const query = `name = '${escapeQ(segment)}' and mimeType = '${FOLDER_MIME}' ` +
        `and '${parentId}' in parents and trashed = false`;
      const list = await this.getJson<{ files?: DriveFile[] }>(
        `${DRIVE_API}/files?spaces=${APP_DATA}` +
        `&q=${encodeURIComponent(query)}&fields=files(id,name)&pageSize=10`,
      );
      const existing = list.files?.[0];

      if (existing) {
        parentId = existing.id;
      } else {
        const created = await this.multipartCreate(
          { name: segment, mimeType: FOLDER_MIME, parents: [parentId] },
          '',
        );
        parentId = created.id;
      }
      this.folderIdCache.set(cacheKey, parentId);
    }
    return parentId;
  }

  private async splitPath(path: string): Promise<{ parentId: string; name: string }> {
    const cleaned = path.replace(/^\/+|\/+$/g, '');
    const slash = cleaned.lastIndexOf('/');
    if (slash < 0) return { parentId: APP_DATA, name: cleaned };
    return {
      parentId: await this.resolveFolderId(cleaned.substring(0, slash)),
      name: cleaned.substring(slash + 1),
    };
  }

  private async findFileId(path: string): Promise<string | null> {
    const { parentId, name } = await this.splitPath(path);
    const query = `name = '${escapeQ(name)}' and '${parentId}' in parents and trashed = false`;
    const list = await this.getJson<{ files?: DriveFile[] }>(
      `${DRIVE_API}/files?spaces=${APP_DATA}` +
      `&q=${encodeURIComponent(query)}&fields=files(id,name)&pageSize=2`,
    );
    return list.files?.[0]?.id ?? null;
  }

  // ─── SyncProvider impl ───────────────────────────────────────────────────

  async listFiles(prefix: string): Promise<RemoteFile[]> {
    if (!this.isAuthenticated()) return [];
    let rootId: string;
    try {
      rootId = await this.resolveFolderId(prefix);
    } catch (e) {
      console.warn(`[drive] listFiles: prefix "${prefix}" not found`, e);
      return [];
    }
    const results: RemoteFile[] = [];
    const queue: Array<{ folderId: string; pathSoFar: string }> = [
      { folderId: rootId, pathSoFar: prefix.replace(/\/+$/, '') },
    ];

    while (queue.length > 0) {
      const { folderId, pathSoFar } = queue.shift()!;
      let pageToken: string | undefined;
      do {
        const query = `'${folderId}' in parents and trashed = false`;
        const url = `${DRIVE_API}/files?spaces=${APP_DATA}` +
          `&q=${encodeURIComponent(query)}` +
          `&fields=nextPageToken,files(id,name,mimeType,modifiedTime,size)` +
          `&pageSize=200` +
          (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '');
        const resp = await this.getJson<{ files?: DriveFile[]; nextPageToken?: string }>(url);
        for (const f of resp.files ?? []) {
          const childPath = pathSoFar ? `${pathSoFar}/${f.name}` : f.name;
          if (f.mimeType === FOLDER_MIME) {
            queue.push({ folderId: f.id, pathSoFar: childPath });
          } else {
            results.push({
              path: childPath,
              remoteModifiedMs: f.modifiedTime ? Date.parse(f.modifiedTime) : 0,
              sizeBytes: f.size ? Number(f.size) : 0,
            });
          }
        }
        pageToken = resp.nextPageToken;
      } while (pageToken);
    }
    return results;
  }

  async readFile(path: string): Promise<string | null> {
    if (!this.isAuthenticated()) return null;
    const id = await this.findFileId(path);
    if (!id) return null;
    return this.getText(`${DRIVE_API}/files/${id}?alt=media`);
  }

  async writeFile(path: string, content: string): Promise<boolean> {
    if (!this.isAuthenticated()) return false;
    try {
      const existingId = await this.findFileId(path);
      if (existingId) {
        await this.mediaUpdate(existingId, content);
      } else {
        const { parentId, name } = await this.splitPath(path);
        await this.multipartCreate({ name, parents: [parentId] }, content);
      }
      return true;
    } catch (e) {
      console.error(`[drive] writeFile(${path}) failed`, e);
      return false;
    }
  }

  async deleteFile(path: string): Promise<boolean> {
    if (!this.isAuthenticated()) return false;
    try {
      const id = await this.findFileId(path);
      if (!id) return true;
      const doFetch = async (): Promise<Response> => fetch(`${DRIVE_API}/files/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      let resp = await doFetch();
      if (resp.status === 401 && await refreshToken()) resp = await doFetch();
      return resp.ok || resp.status === 404;
    } catch (e) {
      console.error(`[drive] deleteFile(${path}) failed`, e);
      return false;
    }
  }

  // ─── Shared folders (F2) ───────────────────────────────────────────────────
  // Visible-Drive I/O for multi-user sharing, on a separately granted full
  // Drive scope token (see googleAuth). The private appdata flow above is
  // never widened. Joining a folder someone else shared requires the full
  // scope — drive.file only reaches files this app created/opened.

  supportsSharedFolders(): boolean { return true; }
  hasSharedScope(): boolean { return hasSharedConsent(); }
  async requestSharedScope(): Promise<boolean> { return requestSharedScope(); }

  /** Fetch with the shared-scope token; one silent re-request on 401. */
  private async sharedFetch(url: string, init?: RequestInit): Promise<Response | null> {
    let token = await getSharedToken();
    if (!token) return null;
    const doFetch = (t: string): Promise<Response> => fetch(url, {
      ...init,
      headers: { ...(init?.headers ?? {}), Authorization: `Bearer ${t}` },
    });
    let resp = await doFetch(token);
    if (resp.status === 401) {
      invalidateSharedToken();
      token = await getSharedToken();
      if (!token) return null;
      resp = await doFetch(token);
    }
    return resp;
  }

  async createSharedFolder(name: string): Promise<SharedFolderInfo | null> {
    const resp = await this.sharedFetch(
      `${DRIVE_API}/files?fields=${encodeURIComponent('id,name,webViewLink')}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, mimeType: FOLDER_MIME }),
      });
    if (!resp?.ok) return null;
    const f = await resp.json() as DriveFile & { webViewLink?: string };
    return { id: f.id, name: f.name ?? name, link: f.webViewLink ?? folderLink(f.id) };
  }

  async getSharedFolderInfo(folderId: string): Promise<SharedFolderInfo | null> {
    const resp = await this.sharedFetch(
      `${DRIVE_API}/files/${encodeURIComponent(folderId)}?fields=${encodeURIComponent('id,name,mimeType,webViewLink,trashed')}`);
    if (!resp?.ok) return null;
    const f = await resp.json() as DriveFile & { webViewLink?: string; trashed?: boolean };
    if (f.mimeType !== FOLDER_MIME || f.trashed) return null;
    return { id: f.id, name: f.name ?? 'Shared folder', link: f.webViewLink ?? folderLink(f.id) };
  }

  async listSharedFolder(folderId: string): Promise<RemoteFile[] | null> {
    const results: RemoteFile[] = [];
    let pageToken: string | undefined;
    do {
      const query = `'${escapeQ(folderId)}' in parents and trashed = false`;
      const url = `${DRIVE_API}/files?q=${encodeURIComponent(query)}` +
        `&fields=${encodeURIComponent('nextPageToken,files(id,name,mimeType,modifiedTime,size)')}` +
        `&pageSize=200` +
        (pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '');
      const resp = await this.sharedFetch(url);
      if (!resp?.ok) return null;
      const json = await resp.json() as { files?: DriveFile[]; nextPageToken?: string };
      for (const f of json.files ?? []) {
        if (f.mimeType === FOLDER_MIME) continue;
        results.push({
          path: f.name,
          remoteModifiedMs: f.modifiedTime ? Date.parse(f.modifiedTime) : 0,
          sizeBytes: f.size ? Number(f.size) : 0,
        });
      }
      pageToken = json.nextPageToken;
    } while (pageToken);
    return results;
  }

  private async findSharedFileId(folderId: string, name: string): Promise<string | null> {
    const query = `name = '${escapeQ(name)}' and '${escapeQ(folderId)}' in parents and trashed = false`;
    const resp = await this.sharedFetch(
      `${DRIVE_API}/files?q=${encodeURIComponent(query)}&fields=${encodeURIComponent('files(id,name)')}&pageSize=2`);
    if (!resp?.ok) return null;
    const json = await resp.json() as { files?: DriveFile[] };
    return json.files?.[0]?.id ?? null;
  }

  async readSharedFile(folderId: string, name: string): Promise<string | null> {
    const id = await this.findSharedFileId(folderId, name);
    if (!id) return null;
    const resp = await this.sharedFetch(`${DRIVE_API}/files/${id}?alt=media`);
    if (!resp?.ok) return null;
    return resp.text();
  }

  async makeFolderLinkShared(folderId: string): Promise<boolean> {
    const resp = await this.sharedFetch(
      `${DRIVE_API}/files/${encodeURIComponent(folderId)}/permissions`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'anyone', role: 'writer' }),
      });
    return resp?.ok ?? false;
  }

  async writeSharedFile(folderId: string, name: string, content: string): Promise<boolean> {
    try {
      const existingId = await this.findSharedFileId(folderId, name);
      if (existingId) {
        const resp = await this.sharedFetch(`${UPLOAD_API}/files/${existingId}?uploadType=media`, {
          method: 'PATCH',
          headers: { 'Content-Type': `${DEFAULT_MIME}; charset=UTF-8` },
          body: content,
        });
        return resp?.ok ?? false;
      }
      const boundary = '----shellshared_' + crypto.randomUUID();
      const body =
        `--${boundary}\r\n` +
        `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
        `${JSON.stringify({ name, parents: [folderId] })}\r\n` +
        `--${boundary}\r\n` +
        `Content-Type: ${DEFAULT_MIME}; charset=UTF-8\r\n\r\n` +
        `${content}\r\n` +
        `--${boundary}--`;
      const resp = await this.sharedFetch(`${UPLOAD_API}/files?uploadType=multipart&fields=id`, {
        method: 'POST',
        headers: { 'Content-Type': `multipart/related; boundary=${boundary}` },
        body,
      });
      return resp?.ok ?? false;
    } catch (e) {
      console.error(`[drive] writeSharedFile(${folderId}/${name}) failed`, e);
      return false;
    }
  }
}

function folderLink(id: string): string {
  return `https://drive.google.com/drive/folders/${id}`;
}

/** Drive Q-language requires single quotes escaped. */
function escapeQ(value: string): string {
  return value.replace(/'/g, "\\'");
}
